import { AppDispatch } from "@Reducers/index";
import { errorCodes } from "@Helpers/errorCodes";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../services/constants";
import { showToastNotification } from "../reducers/toastSlice";
import {
  setTaskData,
  setTaskCost,
  setTaskActualCost,
  setTaskEarnedValue,
  setTaskTypes,
  setTaskDetails,
  setScheduleDetails,
  deleteScheduleDetails,
  setScheduleError,
  setScheduleSliderTime,
  setSelectionSetTaskElements,
  setSimulationDisplaySettings,
} from "../reducers/Tasks";
import type {
  TaskRecord,
  SaveTasksDataRequest,
  SaveTasksDataResponse,
  GetTasksByPortalRequest,
  GetTasksByPortalResponse,
  GetTasksByProjectRequest,
  GetTasksByProjectResponse,
  GetTaskDetailsRequest,
  GetTaskDetailsResponse,
  SaveScheduleDataRequest,
  SaveScheduleDataResponse,
  DeleteScheduleRequest,
  DeleteScheduleResponse,
  GetScheduleDetailsRequest,
  GetScheduleDetailsResponse,
  SaveTaskTypeRequest,
  SaveTaskTypeResponse,
  DeleteTaskTypeRequest,
  DeleteTaskTypeResponse,
  GetTaskTypesRequest,
  GetTaskTypesResponse,
  SaveScheduleFileRequest,
  SaveScheduleFileResponse,
  UpgradeScheduleFileVersionRequest,
  UpgradeScheduleFileVersionResponse,
  DeleteScheduleFileRequest,
  DeleteScheduleFileResponse,
  AssignTasksToScheduleRequest,
  AssignTasksToScheduleResponse,
  ImportScheduleTasksNewRequest,
  ImportScheduleTasksNewResponse,
} from "@ServerTypes/tasks/tasks.schema";

export {
  setTaskData,
  setTaskCost,
  setTaskActualCost,
  setTaskEarnedValue,
  setTaskTypes,
  setTaskDetails,
  setScheduleDetails,
  deleteScheduleDetails as clearScheduleDetails,
  setScheduleSliderTime,
  setSelectionSetTaskElements,
  setSimulationDisplaySettings,
};
import { t, untranslated } from "@i18n";
import type { Exact } from "@Helpers/types/exact";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

export function saveTaskData<T extends Exact<SaveTasksDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const res = await axiosWrapper.post<SaveTasksDataResponse>(`${getBackendUrl()}/tasks/saveTasksData`, data);

    const displayTask = data.title;
    const action = data._id ? "edited" : "created";
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t(`{{task}} has been ${action} successfully!`, { task: displayTask }),
        }),
      );
    }
  };
}

// NOTE: This function calls different endpoints based on params, each with different response shapes
// Backend returns { status, result } for getTasksDataByPortal/Project, { status, data } for getTaskDetails
// Frontend expects { data, metadata } - pre-existing mismatch documented in schema
interface GetTaskDataParams {
  byPortal?: boolean;
  projectId?: ObjectIdString;
  taskId?: ObjectIdString;
}

export function getTaskData(params: GetTaskDataParams) {
  return async (dispatch: AppDispatch) => {
    let urlParams = "";
    let postData: GetTasksByPortalRequest | GetTasksByProjectRequest | GetTaskDetailsRequest = { filter: {} };

    if (params.byPortal) {
      urlParams = "/tasks/getTasksDataByPortal";
      postData = {
        filter: {
          limit: 5,
        },
      };
    } else if (params.projectId && params.projectId !== "undefined") {
      urlParams = "/tasks/getTasksDataByProject";
      postData = {
        projectId: params.projectId,
        filter: {
          limit: 3,
        },
      };
    } else if (params.taskId) {
      urlParams = "/tasks/getTaskDetails";
      postData = {
        taskId: params.taskId,
      };
    }

    const response = await axiosWrapper.post<
      GetTasksByPortalResponse | GetTasksByProjectResponse | GetTaskDetailsResponse
    >(`${getBackendUrl()}${urlParams}`, postData);
    if (response.data.status) {
      // NOTE: Pre-existing mismatch - backend returns result/data, frontend expects data+metadata
      // KNOWN BUG, preserved verbatim: `setTaskData`'s payload is `TaskRecord[]`, but this merges
      // two OBJECTS into an object literal — the array-ness is lost and only the numeric-ish keys
      // survive downstream. SCHEMA GAP too: the response schema declares neither `data` nor
      // `metadata`. Both assertions are erased; reshaping either side would change behaviour.
      dispatch(
        setTaskData({
          ...(response.data as typeof response.data & { data?: TaskRecord[]; metadata?: object }).data,
          ...(response.data as typeof response.data & { data?: TaskRecord[]; metadata?: object }).metadata,
        } as TaskRecord[]),
      );
    }
  };
}

export function getTaskDetails(params: { taskId: ObjectIdString }) {
  return async (dispatch: AppDispatch) => {
    const urlParams = "/tasks/getTaskDetails";
    const postData: GetTaskDetailsRequest = {
      taskId: params.taskId,
    };

    const response = await axiosWrapper.post<GetTaskDetailsResponse>(`${getBackendUrl()}${urlParams}`, postData);
    if (response.data.status) {
      dispatch(setTaskDetails({ ...(response.data.data as Record<string, unknown>) }));
    }
  };
}

export function saveScheduleData<T extends Exact<SaveScheduleDataRequest, T>>(
  data: T,
): Promise<{ data: SaveScheduleDataResponse }> {
  const res = axiosWrapper.post<SaveScheduleDataResponse>(`${getBackendUrl()}/schedule/saveScheduleData`, data);
  return res;
}

export async function deleteSchedule<T extends Exact<DeleteScheduleRequest, T>>(
  data: T,
): Promise<{ data: DeleteScheduleResponse }> {
  const response = await axiosWrapper.post<DeleteScheduleResponse>(`${getBackendUrl()}/schedule/deleteSchedule`, data);
  return response;
}

export function getScheduleDetails<T extends Exact<GetScheduleDetailsRequest, T>>(params: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetScheduleDetailsResponse>(
      `${getBackendUrl()}/schedule/getScheduleDetails`,
      {
        scheduleId: params.scheduleId,
        scheduleVersionId: params.scheduleVersionId,
      },
    );

    if (response.data.status && response.data.data) {
      dispatch(setScheduleError(false));
      dispatch(setScheduleDetails(response.data.data));
    } else {
      dispatch(setScheduleError(response.data.errorCode || errorCodes.SOMETHING_WENT_WRONG));
    }
  };
}

export function saveTaskTypeData<T extends Exact<SaveTaskTypeRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<SaveTaskTypeResponse>(`${getBackendUrl()}/schedule/saveTaskType`, data);
    if (!response.data.status) {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    dispatch(
      showToastNotification({
        content: data._id
          ? t("Task type has been edited successfully!")
          : t("Task type has been created successfully!"),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };
}

export function deleteTaskType<T extends Exact<DeleteTaskTypeRequest, T>>(data: T) {
  return async (): Promise<{ data: DeleteTaskTypeResponse }> => {
    const res = await axiosWrapper.post<DeleteTaskTypeResponse>(`${getBackendUrl()}/schedule/deleteTaskType`, data);
    return res;
  };
}

// NOTE: Schema requires scheduleId and where[], but frontend only passes projectId
// This appears to be a pre-existing usage pattern that may rely on lenient backend validation
export function getTaskTypes(params: { projectId: ObjectIdString; scheduleId?: ObjectIdString }) {
  return async (dispatch: AppDispatch) => {
    const urlParams = "/schedule/getTaskTypes";
    const postData: Partial<GetTaskTypesRequest> = {
      projectId: params.projectId,
      scheduleId: params.scheduleId,
      where: [],
    };
    const response = await axiosWrapper.post<GetTaskTypesResponse>(`${getBackendUrl()}${urlParams}`, postData);
    if (response.data.result?.length) {
      dispatch(setTaskTypes(response.data.result));
    }
  };
}

export function saveScheduleFile<T extends Exact<SaveScheduleFileRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<SaveScheduleFileResponse>(
      `${getBackendUrl()}/schedule/saveScheduleFile`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("File has been added successfully!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}

export function upgradeScheduleFileVersion<T extends Exact<UpgradeScheduleFileVersionRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<UpgradeScheduleFileVersionResponse>(
      `${getBackendUrl()}/schedule/upgradeScheduleFileVersion`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("File has been upgraded successfully!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}
export function deleteScheduleFile<T extends Exact<DeleteScheduleFileRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<DeleteScheduleFileResponse>(
      `${getBackendUrl()}/schedule/deleteScheduleFile`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("File has been deleted successfully!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}

export function assignTasksToSchedule<T extends Exact<AssignTasksToScheduleRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    await axiosWrapper.post<AssignTasksToScheduleResponse>(`${getBackendUrl()}/tasks/assignTasksToSchedule`, data);
    dispatch(
      showToastNotification({
        content: t("Tasks have been successfully added"),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };
}

export async function importScheduleTasks<T extends Exact<ImportScheduleTasksNewRequest, T>>(
  data: T,
): Promise<{ data: ImportScheduleTasksNewResponse }> {
  const response = await axiosWrapper.post<ImportScheduleTasksNewResponse>(
    `${getBackendUrl()}/schedule/importScheduleTasksNew`,
    data,
  );
  return response;
}

export async function importScheduleTasksNew<T extends Exact<ImportScheduleTasksNewRequest, T>>(
  data: T,
): Promise<{ data: ImportScheduleTasksNewResponse }> {
  const response = await axiosWrapper.post<ImportScheduleTasksNewResponse>(
    `${getBackendUrl()}/schedule/importScheduleTasksNew`,
    data,
  );
  return response;
}
